import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { TaskRead } from "@/api/types";
import { rememberedTask, withOpenTask } from "@/lib/taskReturn";

/**
 * The task open in a page's TaskDetailSheet, also written to the page's
 * history entry so that coming back to the page reopens it (see
 * `lib/taskReturn`). `host` names this drawer among the page's drawers.
 * `tasks`, the page's own list, replaces a reopened task once it loads:
 * the copy in history dates from when the page was left.
 */
export function useOpenTask<T extends TaskRead>(
  host: string,
  tasks?: readonly T[],
): [T | null, (task: T | null) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const [task, setTask] = useState<T | null>(() =>
    rememberedTask<T>(location.state, host),
  );
  const fromHistory = useRef(task !== null);
  const here = useRef(location);
  here.current = location;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!fromHistory.current || !task || !tasks) return;
    const current = tasks.find((t) => t.id === task.id);
    if (!current) return;
    fromHistory.current = false;
    setTask(current);
  }, [task, tasks]);

  const show = useCallback(
    (next: T | null) => {
      fromHistory.current = false;
      setTask(next);
      // A save that lands after the page was left must not rewrite the
      // history entry of the page the user went to.
      if (!mounted.current) return;
      const { pathname, search, hash, state } = here.current;
      navigate(
        { pathname, search, hash },
        { replace: true, state: withOpenTask(state, host, next) },
      );
    },
    [host, navigate],
  );

  return [task, show];
}
